/**
 * Onboarding hub route — smart router and track chooser for new users.
 *
 * Evaluates the authenticated user's state and either redirects them
 * to the appropriate destination or presents two onboarding tracks:
 *
 * 1. **Profile incomplete** (no name) → redirect to `/onboard/profile`.
 * 2. **Has scopes** (memberships or enrollments exist) → redirect to
 *    the appropriate zone via {@link defaultRedirectForScopes}.
 * 3. **Profile complete, no scopes** → render the track chooser:
 *    - **Create Organization** — self-serve admin signup.
 *    - **Join with Invite Code** — join an existing org or class.
 *
 * This route is wrapped in {@link AuthGuard} (via the parent layout
 * route in `app.tsx`) but intentionally has **no** {@link ScopeGuard}
 * — new users with zero scopes must be able to access it.
 *
 * @module routes/onboard-hub
 */

import { defaultRedirectForScopes } from '@learnai/api/auth';
import { useAuth } from '@learnai/api/auth-provider';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@learnai/ui/components/ui/card';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { BuildingIcon, LogOutIcon, TicketIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrackOption {
	title: string;
	description: string;
	icon: React.ReactNode;
	href: string;
	accentBg: string;
	accentText: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TRACKS: TrackOption[] = [
	{
		title: 'Set up a new organization',
		description:
			"Create a school, district, or institution. You'll become the owner and can invite staff and students.",
		icon: <BuildingIcon className="size-6" />,
		href: '/onboard/create-org',
		accentBg: 'bg-primary/10',
		accentText: 'text-primary',
	},
	{
		title: 'Join with an invite code',
		description: 'Already have an invite? Enter the code you received to join an existing organization or class.',
		icon: <TicketIcon className="size-6" />,
		href: '/onboard/join',
		accentBg: 'bg-success-muted',
		accentText: 'text-success',
	},
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardHub() {
	const navigate = useNavigate();
	const { user, scopes, isLoading: authLoading, profileComplete, logout } = useAuth();

	// -------------------------------------------------------------------
	// Evaluate user state — redirect if they don't belong here
	// -------------------------------------------------------------------

	useEffect(() => {
		if (authLoading || !user) return;

		// Profile incomplete → must complete before going to a zone or
		// choosing a track. Pass returnTo so the user lands back on the
		// hub afterwards.
		if (!profileComplete) {
			navigate('/onboard/profile?returnTo=/onboard', { replace: true });
			return;
		}

		// Profile complete and has scopes → redirect to their zone.
		if (!scopes.isEmpty()) {
			window.location.href = defaultRedirectForScopes(scopes);
		}
	}, [authLoading, user, profileComplete, scopes, navigate]);

	// -------------------------------------------------------------------
	// Guards
	// -------------------------------------------------------------------

	// Show spinner while loading or while a redirect is queued.
	if (authLoading || !profileComplete || !scopes.isEmpty()) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		);
	}

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------

	const displayName = user?.name?.split(/\s+/)[0] ?? 'there';

	return (
		<div className="relative flex min-h-screen w-full items-center justify-center px-4 py-12">
			<nav className="absolute top-6 left-6">
				<a href="/">
					<BrandLogo size="sm" />
				</a>
			</nav>

			<div className="flex w-full max-w-2xl flex-col items-center gap-8">
				{/* ── Header ─────────────────────────────────────────── */}
				<div className="flex flex-col items-center gap-2 text-center">
					<h1 className="text-3xl font-bold tracking-tight">Hey {displayName}, how would you like to get started?</h1>
					<p className="max-w-md text-balance text-muted-foreground">
						Choose your path below. You can always do the other one later from your dashboard.
					</p>
				</div>

				{/* ── Track cards ─────────────────────────────────────── */}
				<div className="grid w-full gap-4 sm:grid-cols-2">
					{TRACKS.map((track) => (
						<Link key={track.href} to={track.href} className="group">
							<Card className="h-full transition-shadow group-hover:shadow-md">
								<CardHeader>
									<div
										className={`flex size-12 items-center justify-center rounded-lg transition-colors ${track.accentBg} ${track.accentText}`}
									>
										{track.icon}
									</div>
									<CardTitle className="mt-4">{track.title}</CardTitle>
									<CardDescription>{track.description}</CardDescription>
								</CardHeader>
								<CardContent>
									<Button variant="outline" className="w-full" tabIndex={-1}>
										Get started
									</Button>
								</CardContent>
							</Card>
						</Link>
					))}
				</div>

				{/* ── Footer ─────────────────────────────────────────── */}
				<div className="flex flex-col items-center gap-3">
					{user && (
						<p className="text-center text-xs text-muted-foreground">
							Signed in as <span className="font-medium text-foreground">{user.email}</span>
						</p>
					)}
					<Button
						variant="ghost"
						type="button"
						onClick={logout}
						className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
					>
						<LogOutIcon className="size-3" />
						Sign out
					</Button>
				</div>
			</div>
		</div>
	);
}
